import { useEffect, useRef, useState } from "react";
import { ChevronDown, List, Plus, Tags, Trash2, TriangleAlert } from "lucide-react";
import { Dialog, DIALOG_EXIT_MS, NotificationModal } from "@/components/modals";
import PointsChip from "@/components/courses/PointsChip";
import PromptField from "@/components/courses/PromptField";
import TaxonomyPicker from "@/components/courses/TaxonomyPicker";
import {
  COURSE_DIALOG_STYLE,
  taxonomyKindLabel,
  type TaxonomyOption,
} from "@/components/courses/CourseMetaFields";
import { useNotification } from "@/contexts/NotificationContext";
import { api, patch, post } from "@/lib/api";
import {
  ANSWER_MODE_LABELS,
  ANSWER_MODES,
  QUESTION_TYPE_GROUPS,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPE_OPTIONS,
  SINGLE_CHOICE_STYLES,
  allowMultipleUploads,
  defaultScoringForType,
  expectedOptionIds,
  modesForQuestionType,
  nextOptionId,
  matchingTextKey,
  normalizeMatchingOptions,
  normalizeQuestionOptions,
  normalizeScoring,
  blankValuesFrom,
  countFillBlanks,
  padBlankValues,
  questionIncompleteReasons,
  showAttemptControls,
  attemptPointsInvalid,
  type AnswerMode,
  type ChoiceOption,
  type CorrectKey,
  type MatchingPair,
  type Scoring,
  type SingleChoiceStyle,
} from "@/lib/questionScoring";

export type QuestionRecord = {
  id: string;
  prompt: string;
  type: string;
  options: (ChoiceOption | MatchingPair)[];
  correct: CorrectKey;
  scoring?: Partial<Scoring> | null;
  presentation?: string | null;
  awardAttemptPoints?: boolean;
  attemptPoints?: number;
  promptFormat?: "plain" | "html";
  allowMultiple?: boolean;
  maxFiles?: number;
  points: number;
  position: number;
  status?: string;
  published?: boolean;
  primaryTaxonomy: string | null;
  taxonomyIds: string[];
};

function AttemptCredit({
  enabled,
  points,
  maxAttempt,
  onToggle,
  onPoints,
  disabled,
}: {
  enabled: boolean;
  points: number;
  maxAttempt: number;
  onToggle: (on: boolean) => void;
  onPoints: (pts: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className={`al-cb-attempt${enabled ? " al-cb-attempt--on" : ""}`}>
      <label title="Partial credit when the answer is non-empty but not fully correct">
        <input type="checkbox" checked={enabled} disabled={disabled} onChange={(e) => onToggle(e.target.checked)} />
        <span>Award points just for attempting?</span>
      </label>
      {enabled ? (
        <PointsChip
          value={points}
          suffix="att"
          title="Attempt points"
          disabled={disabled}
          max={maxAttempt}
          onChange={onPoints}
        />
      ) : null}
    </div>
  );
}

function singleChoiceHint(style: string) {
  if (style === "dropdown") return "Learner picks one option from a dropdown.";
  if (style === "yes_no") return "Learner answers Yes or No.";
  if (style === "true_false") return "Learner answers True or False.";
  return "Learner picks one option from a list.";
}

function isBinaryStyle(style: string) {
  return style === "yes_no" || style === "true_false";
}

function binaryLabels(style: string) {
  return style === "true_false" ? ["True", "False"] : ["Yes", "No"];
}

function initialType(question: QuestionRecord | null) {
  const type = question?.type ?? "multiple_choice";
  return type === "true_false" || type === "yes_no" ? "single_choice" : type;
}

function initialPresentation(question: QuestionRecord | null): SingleChoiceStyle {
  if (question?.type === "true_false") return "true_false";
  if (question?.type === "yes_no") return "yes_no";
  const p = question?.presentation;
  return p === "dropdown" || p === "yes_no" || p === "true_false" || p === "radio" ? p : "radio";
}

function asChoiceOptions(list: unknown, type: string, presentation?: string | null): ChoiceOption[] {
  return normalizeQuestionOptions(list, type, presentation).filter(
    (row): row is ChoiceOption => "label" in row,
  );
}

function initialAllowMultiple(question: QuestionRecord | null) {
  if (!question || question.type !== "upload") return true;
  return allowMultipleUploads(question.scoring, "upload") && question.allowMultiple !== false && question.maxFiles !== 1;
}

function initialPublished(question: QuestionRecord | null) {
  if (!question) return true;
  return question.published === true || question.status === "published";
}

function emptyChoices(): ChoiceOption[] {
  return [
    { id: 1, label: "" },
    { id: 2, label: "" },
  ];
}

export default function QuestionEditor({
  activityId,
  question,
  coursePrimaryTaxonomy,
  readOnly,
  itemLabel,
  onBack,
  onSaved,
  proposeOnly,
  showError,
}: {
  activityId: string;
  question: QuestionRecord | null;
  coursePrimaryTaxonomy: string | null;
  readOnly: boolean;
  itemLabel: string;
  onBack: () => void;
  onSaved: () => Promise<void>;
  proposeOnly?: boolean;
  showError: (title: string, err: unknown) => void;
}) {
  const [type, setType] = useState(() => initialType(question));
  const [prompt, setPrompt] = useState(question?.prompt ?? "");
  const [promptFormat, setPromptFormat] = useState<"plain" | "html">(question?.promptFormat === "html" ? "html" : "plain");
  const [points, setPoints] = useState(question?.points ?? 2);
  const [primaryTaxonomy, setPrimaryTaxonomy] = useState(
    question?.primaryTaxonomy || coursePrimaryTaxonomy || "",
  );
  const [taxonomyIds, setTaxonomyIds] = useState<string[]>(question?.taxonomyIds ?? []);
  const [taxonomyOptions, setTaxonomyOptions] = useState<TaxonomyOption[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [standardOptions, setStandardOptions] = useState<TaxonomyOption[]>([]);
  const [standardLoading, setStandardLoading] = useState(true);
  const [options, setOptions] = useState<ChoiceOption[]>(() => {
    const style = initialPresentation(question);
    const existing = asChoiceOptions(
      question?.options,
      question?.type ?? "multiple_choice",
      question?.presentation ?? style,
    );
    if (existing.length) return existing;
    if (isBinaryStyle(style)) return asChoiceOptions(binaryLabels(style), "single_choice", style);
    return emptyChoices();
  });
  const [pairs, setPairs] = useState<MatchingPair[]>(() => {
    const existing = normalizeMatchingOptions(question?.options);
    return existing.length ? existing : [
      { id: 1, left: "", right: "" },
      { id: 2, left: "", right: "" },
    ];
  });
  const [correctIds, setCorrectIds] = useState<number[]>(() => expectedOptionIds(question));
  const [correctAnswer, setCorrectAnswer] = useState(question?.correct?.answer ?? "");
  const [scoring, setScoring] = useState<Scoring>(() => {
    const type = question?.type ?? "multiple_choice";
    const next = normalizeScoring(question?.scoring, type);
    if (type === "fill_blank") {
      const values = blankValuesFrom(next.textRule, question?.correct);
      next.textRule = { ...next.textRule, value: values[0] || "", values };
    } else if (
      (type === "short_answer" || type === "long_answer") &&
      !next.textRule.value &&
      question?.correct?.answer
    ) {
      next.textRule = { ...next.textRule, value: String(question.correct.answer) };
    }
    return next;
  });
  const [presentation, setPresentation] = useState<SingleChoiceStyle>(() => initialPresentation(question));
  const [awardAttemptPoints, setAwardAttemptPoints] = useState(question?.awardAttemptPoints === true);
  const [attemptPoints, setAttemptPoints] = useState(question?.attemptPoints ?? 1);
  const [allowMultipleFiles, setAllowMultipleFiles] = useState(() => initialAllowMultiple(question));
  const [published, setPublished] = useState(() => initialPublished(question));
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const [taxonomyOpen, setTaxonomyOpen] = useState(false);
  const [subjectEditorOpen, setSubjectEditorOpen] = useState(false);
  const [standardEditorOpen, setStandardEditorOpen] = useState(false);
  const [taxonomyListOpen, setTaxonomyListOpen] = useState(false);
  const taxonomyPickerOpen = subjectEditorOpen || standardEditorOpen;
  const { notice } = useNotification();
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const wasLive = Boolean(question && (question.published === true || question.status === "published"));
  const closeTimer = useRef<number | null>(null);
  const isProposed = question?.status === "proposed" || (!question && proposeOnly);
  const subjectRow = taxonomyOptions.find((row) => row.id === primaryTaxonomy) || null;
  const taxonomyTagged = Boolean(primaryTaxonomy || taxonomyIds.length);
  const snapshot = JSON.stringify({
    type,
    prompt,
    promptFormat,
    points,
    primaryTaxonomy,
    taxonomyIds,
    options,
    pairs,
    correctIds,
    correctAnswer,
    scoring,
    presentation,
    awardAttemptPoints,
    attemptPoints,
    allowMultipleFiles,
    published,
  });
  const [initialSnapshot] = useState(snapshot);
  const dirty = snapshot !== initialSnapshot;
  const closeTaxonomy = () => {
    if (taxonomyPickerOpen) return;
    setTaxonomyOpen(false);
    setTaxonomyListOpen(false);
  };

  const finishClose = () => {
    if (closeTimer.current !== null) return;
    setOpen(false);
    closeTimer.current = window.setTimeout(onBack, DIALOG_EXIT_MS);
  };

  const requestClose = () => {
    if (dirty && !readOnly) {
      setDiscardOpen(true);
      return;
    }
    finishClose();
  };

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const load = async (
          url: string,
          setRows: (rows: TaxonomyOption[]) => void,
          setDone: (loading: boolean) => void,
        ) => {
          try {
            const envelope = await api<{ success: boolean; data: TaxonomyOption[] }>(url);
            if (!cancelled) setRows(Array.isArray(envelope.data) ? envelope.data : []);
          } catch {
            /* picker still saves ids without titles */
          } finally {
            if (!cancelled) setDone(false);
          }
        };
        await Promise.all([
          load("/api/courses/taxonomy", setTaxonomyOptions, setTaxonomyLoading),
          load("/api/courses/taxonomy?type=standard", setStandardOptions, setStandardLoading),
        ]);
      } catch {
        if (!cancelled) {
          setTaxonomyLoading(false);
          setStandardLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyType = (next: string) => {
    setType(next);
    setScoring(defaultScoringForType(next));
    setCorrectIds([]);
    setAwardAttemptPoints(false);
    setAttemptPoints(1);
    setAllowMultipleFiles(true);
    if (next === "single_choice") {
      setPresentation("radio");
      setOptions((prev) => (prev.length >= 2 ? prev : emptyChoices()));
      setCorrectAnswer("");
    } else if (next === "multiple_choice") {
      setOptions((prev) => (prev.length >= 2 ? prev : emptyChoices()));
      setCorrectAnswer("");
    } else if (next === "matching") {
      setPairs((prev) =>
        prev.length >= 2
          ? prev
          : [
              { id: 1, left: "", right: "" },
              { id: 2, left: "", right: "" },
            ],
      );
      setCorrectAnswer("");
    } else {
      setCorrectAnswer("");
    }
    setTypePickerOpen(false);
  };

  const applyPresentation = (style: SingleChoiceStyle) => {
    setPresentation(style);
    if (isBinaryStyle(style)) {
      const labels = binaryLabels(style);
      setOptions((prev) =>
        labels.map((label, i) => ({
          id: prev[i]?.id ?? (i === 0 ? 1 : nextOptionId(prev.slice(0, i))),
          label,
        })),
      );
      setCorrectIds((prev) => prev.filter((id) => id === options[0]?.id || id === options[1]?.id).slice(0, 1));
    } else if (
      options.length < 2 ||
      (options.length === 2 &&
        ((options[0].label === "Yes" && options[1].label === "No") ||
          (options[0].label === "True" && options[1].label === "False")))
    ) {
      setOptions((prev) => [
        { id: prev[0]?.id ?? 1, label: "" },
        { id: prev[1]?.id ?? 2, label: "" },
      ]);
    }
  };

  const patchScoring = (partial: Partial<Scoring>) => {
    const next = normalizeScoring({ ...scoring, ...partial }, type);
    if (awardAttemptPoints && !showAttemptControls(type, next)) {
      setAwardAttemptPoints(false);
      setAttemptPoints(1);
      notice({
        type: "warning",
        title: "Attempt points removed",
        subtext:
          next.answerMode === ANSWER_MODES.MANUAL
            ? "Manual answers are scored by a teacher, so attempt credit does not apply."
            : "Free answers already earn full points, so attempt credit does not apply.",
        duration: 7000,
      });
    }
    setScoring(next);
  };

  const isSingle = type === "single_choice";
  const showChoices = type === "multiple_choice" || (isSingle && !isBinaryStyle(presentation));
  const modes = modesForQuestionType(type);
  const showScoring = type !== "directions" && modes.length > 0;
  const showAttempts = showAttemptControls(type, scoring);
  const blankCount = type === "fill_blank" ? Math.max(1, countFillBlanks(prompt)) : 0;
  const blankAnswers =
    type === "fill_blank"
      ? padBlankValues(
          scoring.textRule.values?.length ? scoring.textRule.values : [scoring.textRule.value],
          blankCount,
        )
      : [];
  const matchingLeftDupes = new Set<string>();
  const matchingRightDupes = new Set<string>();
  if (type === "matching") {
    const leftCounts = new Map<string, number>();
    const rightCounts = new Map<string, number>();
    for (const row of pairs) {
      const left = matchingTextKey(row.left);
      const right = matchingTextKey(row.right);
      if (left) leftCounts.set(left, (leftCounts.get(left) || 0) + 1);
      if (right) rightCounts.set(right, (rightCounts.get(right) || 0) + 1);
    }
    for (const [key, n] of leftCounts) if (n > 1) matchingLeftDupes.add(key);
    for (const [key, n] of rightCounts) if (n > 1) matchingRightDupes.add(key);
  }
  const draftGaps = questionIncompleteReasons({
    type,
    prompt,
    options: type === "matching" ? pairs : options,
    correct:
      type === "multiple_choice"
        ? { optionIds: correctIds }
        : type === "single_choice" && correctIds[0] != null
          ? { optionId: correctIds[0] }
          : type === "fill_blank"
            ? { answer: blankAnswers[0] || "", blanks: blankAnswers }
            : {},
    scoring:
      type === "fill_blank"
        ? { ...scoring, textRule: { ...scoring.textRule, value: blankAnswers[0] || "", values: blankAnswers } }
        : scoring,
    presentation: type === "single_choice" ? presentation : undefined,
    points,
    awardAttemptPoints,
    attemptPoints,
  });
  const incompleteLive = !isProposed && published && draftGaps.length > 0;
  const willRevertToDraft = incompleteLive && wasLive;
  const liveBlocked = incompleteLive && !wasLive;

  const persist = async (live: boolean, revertedToDraft = false) => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        type,
        prompt,
        promptFormat,
        points,
        primaryTaxonomy: primaryTaxonomy || null,
        taxonomyIds,
        scoring,
        presentation: type === "single_choice" ? presentation : undefined,
        awardAttemptPoints,
        attemptPoints: awardAttemptPoints ? attemptPoints : 0,
        published: isProposed ? undefined : live,
      };
      if (type === "upload") {
        payload.allowMultiple = allowMultipleFiles;
        payload.scoring = { ...scoring, allowMultiple: allowMultipleFiles };
      }
      if (type === "multiple_choice") {
        payload.options = options;
        payload.correct = correctIds.length ? { optionIds: [...correctIds].sort((a, b) => a - b) } : {};
        payload.scoring = { ...scoring, correctOptions: correctIds };
      } else if (type === "single_choice") {
        payload.options = isBinaryStyle(presentation)
          ? asChoiceOptions(binaryLabels(presentation), "single_choice", presentation).map((row, i) => ({
              id: options[i]?.id ?? row.id,
              label: row.label,
            }))
          : options;
        payload.correct = correctIds[0] != null ? { optionId: correctIds[0] } : {};
        payload.scoring = { ...scoring, correctOptions: correctIds.slice(0, 1) };
      } else if (type === "matching") {
        payload.options = pairs.filter((row) => String(row.left).trim() || String(row.right).trim());
        payload.correct = {};
      } else if (type === "fill_blank") {
        const values = padBlankValues(
          scoring.textRule.values?.length ? scoring.textRule.values : [scoring.textRule.value],
          Math.max(1, countFillBlanks(prompt)),
        );
        payload.options = [];
        payload.correct =
          scoring.answerMode === ANSWER_MODES.TEXT
            ? { answer: values[0] || "", blanks: values }
            : { answer: correctAnswer, blanks: values };
        payload.scoring = { ...scoring, textRule: { ...scoring.textRule, value: values[0] || "", values } };
      } else if (type === "short_answer" || type === "long_answer") {
        payload.options = [];
        payload.correct = { answer: scoring.answerMode === ANSWER_MODES.TEXT ? scoring.textRule.value : correctAnswer };
      } else if (type === "number") {
        payload.options = [];
        payload.correct = {
          answer: scoring.answerMode === ANSWER_MODES.NUMBER_EXACT ? String(scoring.numberRule.exact) : "",
        };
      } else if (type === "date") {
        payload.options = [];
        payload.correct = {
          answer: scoring.answerMode === ANSWER_MODES.DATE_EXACT ? String(scoring.dateRule.exact) : "",
        };
      } else {
        payload.options = [];
        payload.correct = {};
      }
      const result = question
        ? await patch<{
            proposed?: boolean;
            data?: { revertedToDraft?: boolean; activityRevertedToDraft?: boolean };
          }>(`/api/activities/questions/${question.id}`, payload)
        : await post<{
            proposed?: boolean;
            data?: { revertedToDraft?: boolean; activityRevertedToDraft?: boolean };
          }>(`/api/activities/${activityId}/questions`, payload);
      if (!result?.proposed && (revertedToDraft || result?.data?.revertedToDraft || result?.data?.activityRevertedToDraft)) {
        notice({
          type: "warning",
          title: "Reverted to Draft",
          subtext: result?.data?.activityRevertedToDraft
            ? "This item is incomplete, so it is no longer published."
            : "The question is incomplete, so it is no longer Live.",
          duration: 7000,
        });
      }
      await onSaved();
      finishClose();
    } catch (err) {
      showError("Could not save the question", err);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (awardAttemptPoints && attemptPointsInvalid(points, true, attemptPoints)) {
      notice({
        type: "warning",
        title: "Attempt points too high",
        subtext: "Attempt points must be less than the question points.",
        duration: 7000,
      });
      return;
    }
    if (incompleteLive) {
      if (wasLive) {
        setRevertOpen(true);
        return;
      }
      notice({
        type: "warning",
        title: "Finish the question first",
        subtext: `${draftGaps.join(" ")} Switch to Draft to save it unfinished.`,
        duration: 7000,
      });
      return;
    }
    await persist(Boolean(!isProposed && published));
  };

  const toggleLive = () => {
    if (published) {
      setPublished(false);
      return;
    }
    if (draftGaps.length) {
      notice({
        type: "warning",
        title: "Finish the question first",
        subtext: draftGaps.join(" ") || "A question has to be complete before it can go Live.",
        duration: 7000,
      });
      return;
    }
    setPublished(true);
  };

  return (
    <>
      <Dialog
        open={open}
        title={question ? "Edit question" : "Add question"}
        subtitle={itemLabel}
        width="40rem"
        maxHeight="min(90vh, 48rem)"
        escapeClose={!typePickerOpen && !taxonomyOpen && !discardOpen && !revertOpen}
        onClose={requestClose}
        headerActions={
          <button
            type="button"
            className={`al-cb-tax-btn${taxonomyTagged ? " al-cb-tax-btn--on" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={taxonomyOpen}
            aria-label="Subject and standard"
            title={
              subjectRow
                ? `${taxonomyKindLabel(subjectRow.type)} · ${subjectRow.title}`
                : "Subject and standard"
            }
            onClick={() => setTaxonomyOpen(true)}
          >
            <Tags size={15} aria-hidden="true" />
          </button>
        }
        slideFooter
        footer={
          !readOnly && (dirty || saving) ? (
            <button
              type="button"
              className="al-cb-btn al-cb-btn--primary al-cb-btn--pulse"
              disabled={saving || liveBlocked}
              title={
                liveBlocked
                  ? "Finish the question or switch to Draft to save it unfinished."
                  : willRevertToDraft
                    ? "Saving will revert this question to Draft because it is incomplete."
                    : undefined
              }
              onClick={() => void save()}
            >
              {saving
                ? proposeOnly && question?.status !== "proposed"
                  ? "PROPOSING…"
                  : "SAVING…"
                : proposeOnly && question?.status !== "proposed"
                  ? "PROPOSE"
                  : "SAVE"}
            </button>
          ) : null
        }
      >

        <div className="al-cb-qedit">
          <div className="al-cb-qbar">
          <div className="al-cb-type-line">
            <button
              type="button"
              className="al-cb-type-badge"
              aria-haspopup="dialog"
              aria-expanded={typePickerOpen}
              disabled={readOnly || saving}
              onClick={() => setTypePickerOpen(true)}
            >
              <span>{QUESTION_TYPE_LABELS[type] || type}</span>
              <ChevronDown size={14} aria-hidden="true" />
            </button>
            {isProposed ? (
              <span className="al-cb-badge al-cb-badge--proposed">Proposed</span>
            ) : (
              <button
                type="button"
                className={`al-cb-badge ${published ? "al-cb-badge--done" : "al-cb-badge--todo"}`}
                disabled={readOnly || saving || (!published && draftGaps.length > 0)}
                aria-pressed={published}
                title={
                  !published && draftGaps.length > 0
                    ? "Finish the question before going Live"
                    : published
                      ? "Students can see this when the activity is live. Click for Draft."
                      : "Hidden from students. Click to go Live."
                }
                onClick={toggleLive}
              >
                {published ? "Live" : "Draft"}
              </button>
            )}
            {isSingle ? (
              <div className="al-cb-style-tabs" role="tablist" aria-label="Choice style">
                {SINGLE_CHOICE_STYLES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    role="tab"
                    aria-selected={presentation === s.id}
                    className={`al-cb-style-tab${presentation === s.id ? " al-cb-style-tab--on" : ""}`}
                    disabled={readOnly || saving}
                    onClick={() => applyPresentation(s.id)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            ) : null}
            {type !== "directions" ? (
              <PointsChip
                value={points}
                suffix="pts"
                title="Points"
                disabled={readOnly || saving}
                onChange={(next) => {
                  setPoints(next);
                  if (awardAttemptPoints && attemptPoints >= next) {
                    setAttemptPoints(Math.max(0, next - 1));
                  }
                  if (next <= 0 && awardAttemptPoints) setAwardAttemptPoints(false);
                }}
              />
            ) : null}
            {type !== "directions" && showAttempts ? (
              <AttemptCredit
                enabled={awardAttemptPoints}
                points={attemptPoints}
                maxAttempt={Math.max(0, points - 1)}
                disabled={readOnly || saving}
                onToggle={(on) => {
                  if (on && points <= 0) {
                    notice({
                      type: "warning",
                      title: "Need question points first",
                      subtext: "Attempt points must be less than the question points.",
                      duration: 7000,
                    });
                    return;
                  }
                  setAwardAttemptPoints(on);
                  if (on) setAttemptPoints(Math.min(Math.max(attemptPoints, 0), Math.max(0, points - 1)));
                }}
                onPoints={setAttemptPoints}
              />
            ) : null}
          </div>
          {!question && !isProposed ? (
            <p className="al-cb-style-hint">
              Starts <strong>Live</strong> once it is complete. Click the badge to save as Draft while you write.
            </p>
          ) : null}
          {isSingle ? <p className="al-cb-style-hint">{singleChoiceHint(presentation)}</p> : null}
          {type === "multiple_choice" ? (
            <p className="al-cb-style-hint">Learners check every option that applies. Full points only if the exact set is selected.</p>
          ) : null}
          {type === "matching" ? (
            <p className="al-cb-style-hint">Each pair is the answer key. Full points only when every match is correct.</p>
          ) : null}
          {type === "fill_blank" ? (
            <p className="al-cb-style-hint">
              One answer per blank, in order. Full points only when every blank is correct.
            </p>
          ) : null}
        </div>

        <PromptField
          value={prompt}
          format={promptFormat}
          disabled={readOnly || saving}
          allowBlanks={type === "fill_blank"}
          onChange={setPrompt}
          onFormatChange={setPromptFormat}
        />

        {showChoices ? (
          <div className="al-cb-options">
            <div className="al-cb-options-head">
              <span className="al-cb-options-hint">
                {scoring.answerMode === ANSWER_MODES.CHOICES
                  ? type === "multiple_choice"
                    ? "Options — check every correct answer"
                    : "Options — mark the correct one"
                  : "Options"}
              </span>
            </div>
            {options.map((option, index) => (
              <div key={option.id} className="al-cb-option-row">
                {scoring.answerMode === ANSWER_MODES.CHOICES && type === "multiple_choice" ? (
                  <input
                    type="checkbox"
                    checked={correctIds.includes(option.id)}
                    disabled={readOnly || saving}
                    onChange={(e) =>
                      setCorrectIds((prev) =>
                        e.target.checked ? [...prev, option.id] : prev.filter((id) => id !== option.id),
                      )
                    }
                  />
                ) : scoring.answerMode === ANSWER_MODES.CHOICES ? (
                  <input
                    type="radio"
                    name="q-correct"
                    checked={correctIds[0] === option.id}
                    disabled={readOnly || saving}
                    onChange={() => setCorrectIds([option.id])}
                  />
                ) : (
                  <span className="al-cb-option-spacer" />
                )}
                <input
                  className="al-cb-input"
                  placeholder={`Option ${index + 1}`}
                  value={option.label}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    setOptions((prev) => prev.map((row) => (row.id === option.id ? { ...row, label: e.target.value } : row)))
                  }
                />
                {!readOnly ? (
                  <button
                    type="button"
                    className="al-cb-icon-btn al-cb-icon-btn--danger"
                    aria-label="Remove option"
                    disabled={options.length <= 2}
                    onClick={() => {
                      setOptions((prev) => prev.filter((row) => row.id !== option.id));
                      setCorrectIds((prev) => prev.filter((id) => id !== option.id));
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                ) : null}
              </div>
            ))}
            {!readOnly ? (
              <button
                type="button"
                className="al-cb-add-link"
                onClick={() => setOptions((prev) => [...prev, { id: nextOptionId(prev), label: "" }])}
              >
                <Plus size={13} aria-hidden="true" />
                Add option
              </button>
            ) : null}
          </div>
        ) : null}

        {isSingle && isBinaryStyle(presentation) && scoring.answerMode === ANSWER_MODES.CHOICES ? (
          <div className="al-cb-correct">
            <span className="al-cb-label">Correct answer</span>
            <div className="al-cb-style-tabs" role="radiogroup">
              {options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={correctIds[0] === option.id}
                  className={`al-cb-style-tab${correctIds[0] === option.id ? " al-cb-style-tab--on" : ""}`}
                  disabled={readOnly || saving}
                  onClick={() => setCorrectIds([option.id])}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {type === "matching" ? (
          <div className="al-cb-options">
            <span className="al-cb-options-hint">
              Each left item and each match must be unique. Extra spaces and
              capitalization do not make them different — students only see the text.
            </span>
            {pairs.map((pair) => {
              const leftKey = matchingTextKey(pair.left);
              const rightKey = matchingTextKey(pair.right);
              const leftDup = Boolean(leftKey && matchingLeftDupes.has(leftKey));
              const rightDup = Boolean(rightKey && matchingRightDupes.has(rightKey));
              return (
              <div key={pair.id} className={`al-cb-pair-row${leftDup || rightDup ? " al-cb-pair-row--dup" : ""}`}>
                <input
                  className={`al-cb-input${leftDup ? " al-cb-input--dup" : ""}`}
                  placeholder="Left"
                  value={pair.left}
                  disabled={readOnly || saving}
                  aria-invalid={leftDup}
                  onChange={(e) =>
                    setPairs((prev) => prev.map((row) => (row.id === pair.id ? { ...row, left: e.target.value } : row)))
                  }
                />
                <input
                  className={`al-cb-input${rightDup ? " al-cb-input--dup" : ""}`}
                  placeholder="Match"
                  value={pair.right}
                  disabled={readOnly || saving}
                  aria-invalid={rightDup}
                  onChange={(e) =>
                    setPairs((prev) => prev.map((row) => (row.id === pair.id ? { ...row, right: e.target.value } : row)))
                  }
                />
                {!readOnly ? (
                  <button
                    type="button"
                    className="al-cb-icon-btn al-cb-icon-btn--danger"
                    aria-label="Remove pair"
                    onClick={() => setPairs((prev) => prev.filter((row) => row.id !== pair.id))}
                  >
                    <Trash2 size={12} />
                  </button>
                ) : null}
              </div>
              );
            })}
            {!readOnly ? (
              <button
                type="button"
                className="al-cb-add-link"
                onClick={() => setPairs((prev) => [...prev, { id: nextOptionId(prev), left: "", right: "" }])}
              >
                <Plus size={13} aria-hidden="true" />
                Add pair
              </button>
            ) : null}
          </div>
        ) : null}

        {type === "upload" ? (
          <div className="al-cb-upload-opts">
            <label className="al-cb-check">
              <input
                type="checkbox"
                checked={allowMultipleFiles}
                disabled={readOnly || saving}
                onChange={(e) => setAllowMultipleFiles(e.target.checked)}
              />
              Allow multiple files
            </label>
            <p className="al-cb-style-hint">
              {allowMultipleFiles
                ? "Learners can attach more than one file. Use Free to credit any upload, or Manual to grade it."
                : "Learners attach one file. Use Free to credit any upload, or Manual to grade it."}
            </p>
          </div>
        ) : null}

        {showScoring ? (
          <div className="al-cb-scoring">
            <span className="al-cb-label">Answer scoring</span>
            <div className="al-cb-scoring-modes" role="radiogroup">
              {modes.map((mode) => (
                <label key={mode} className="al-cb-scoring-mode">
                  <input
                    type="radio"
                    name="q-score-mode"
                    checked={scoring.answerMode === mode}
                    disabled={readOnly || saving}
                    onChange={() => patchScoring({ answerMode: mode as AnswerMode })}
                  />
                  {ANSWER_MODE_LABELS[mode] || mode}
                </label>
              ))}
            </div>

            {scoring.answerMode === ANSWER_MODES.TEXT ? (
              <div className="al-cb-scoring-rule">
                <select
                  className="al-cb-select al-cb-select--inline"
                  value={scoring.textRule.match}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({
                      textRule: { ...scoring.textRule, match: e.target.value === "equals" ? "equals" : "includes" },
                    })
                  }
                >
                  <option value="includes">Includes text</option>
                  <option value="equals">Equals exactly</option>
                </select>
                {type === "fill_blank" ? (
                  <div className="al-cb-blank-answers">
                    {blankAnswers.map((value, index) => (
                      <label key={index} className="al-cb-blank-answer">
                        <span className="al-cb-blank-answer-n">Blank {index + 1}</span>
                        <input
                          className="al-cb-input"
                          value={value}
                          disabled={readOnly || saving}
                          placeholder={`Answer ${index + 1}`}
                          onChange={(e) => {
                            const next = [...blankAnswers];
                            next[index] = e.target.value;
                            patchScoring({
                              textRule: { ...scoring.textRule, value: next[0] || "", values: next },
                            });
                          }}
                        />
                      </label>
                    ))}
                  </div>
                ) : (
                  <input
                    className="al-cb-input"
                    value={scoring.textRule.value}
                    disabled={readOnly || saving}
                    placeholder="Match value"
                    onChange={(e) =>
                      patchScoring({ textRule: { ...scoring.textRule, value: e.target.value } })
                    }
                  />
                )}
                <label className="al-cb-check al-cb-check--tight">
                  <input
                    type="checkbox"
                    checked={scoring.textRule.caseSensitive}
                    disabled={readOnly || saving}
                    onChange={(e) =>
                      patchScoring({ textRule: { ...scoring.textRule, caseSensitive: e.target.checked } })
                    }
                  />
                  Case sensitive
                </label>
              </div>
            ) : null}

            {scoring.answerMode === ANSWER_MODES.NUMBER_EXACT ? (
              <label className="al-cb-scoring-inline">
                Exact
                <input
                  type="number"
                  className="al-cb-input"
                  value={scoring.numberRule.exact}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({ numberRule: { ...scoring.numberRule, exact: e.target.value } })
                  }
                />
              </label>
            ) : null}

            {scoring.answerMode === ANSWER_MODES.NUMBER_RANGE ? (
              <div className="al-cb-scoring-rule">
                <input
                  type="number"
                  className="al-cb-input"
                  placeholder="Min"
                  value={scoring.numberRule.min}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({ numberRule: { ...scoring.numberRule, min: e.target.value } })
                  }
                />
                <span>to</span>
                <input
                  type="number"
                  className="al-cb-input"
                  placeholder="Max"
                  value={scoring.numberRule.max}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({ numberRule: { ...scoring.numberRule, max: e.target.value } })
                  }
                />
              </div>
            ) : null}

            {scoring.answerMode === ANSWER_MODES.DATE_EXACT ? (
              <label className="al-cb-scoring-inline">
                Exact
                <input
                  type="date"
                  className="al-cb-input al-cb-date-input"
                  value={scoring.dateRule.exact}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({ dateRule: { ...scoring.dateRule, exact: e.target.value } })
                  }
                />
              </label>
            ) : null}

            {scoring.answerMode === ANSWER_MODES.DATE_RANGE ? (
              <div className="al-cb-scoring-rule">
                <input
                  type="date"
                  className="al-cb-input al-cb-date-input"
                  value={scoring.dateRule.min}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({ dateRule: { ...scoring.dateRule, min: e.target.value } })
                  }
                />
                <span>to</span>
                <input
                  type="date"
                  className="al-cb-input al-cb-date-input"
                  value={scoring.dateRule.max}
                  disabled={readOnly || saving}
                  onChange={(e) =>
                    patchScoring({ dateRule: { ...scoring.dateRule, max: e.target.value } })
                  }
                />
              </div>
            ) : null}

            {scoring.answerMode === ANSWER_MODES.FREE ? (
              <p className="al-cb-style-hint">Any non-empty answer earns full points.</p>
            ) : null}
            {scoring.answerMode === ANSWER_MODES.PAIRS ? (
              <p className="al-cb-style-hint">
                Auto-scored from the pairs. All matches must be correct for full points.
              </p>
            ) : null}
            {scoring.answerMode === ANSWER_MODES.MANUAL ? (
              <p className="al-cb-style-hint">Manual grade — stays pending until a teacher reviews it.</p>
            ) : null}
          </div>
        ) : null}

        {draftGaps.length ? (
          <p className="al-cb-q-gaps" role="status">
            Incomplete — {draftGaps.join(" ")}
            {willRevertToDraft
              ? " Saving will revert this question to Draft so students will not see it."
              : liveBlocked
                ? " Switch to Draft to save it unfinished."
                : ""}
          </p>
        ) : null}
        </div>
      </Dialog>

      <NotificationModal
        isOpen={typePickerOpen}
        type="neutral"
        icon={<List />}
        title="Question type"
        message="What should the learner enter or pick?"
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1300}
        style={COURSE_DIALOG_STYLE}
        onClose={() => setTypePickerOpen(false)}
      >
        <div className="al-cb-qtype-groups">
          {QUESTION_TYPE_GROUPS.map((group, gi) => (
            <div key={group.key} className="al-cb-qtype-group">
              {gi > 0 ? <div className="al-cb-qtype-sep" role="separator" /> : null}
              <p className="al-cb-qtype-group-label">{group.label}</p>
              <div className="al-cb-qtype-grid">
                {QUESTION_TYPE_OPTIONS.filter((t) => t.group === group.key).map((t) => {
                  const selected = type === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`al-cb-qtype-card${selected ? " al-cb-qtype-card--selected" : ""}`}
                      onClick={() => applyType(t.id)}
                    >
                      <span className="al-cb-qtype-card-title">{t.label}</span>
                      <span className="al-cb-qtype-card-desc">{t.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={taxonomyOpen}
        type="neutral"
        icon={<Tags />}
        title="Subject and standard"
        message={null}
        overlay
        overlayClose={!taxonomyPickerOpen}
        escapeClose={!taxonomyPickerOpen}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1250}
        style={COURSE_DIALOG_STYLE}
        onClose={closeTaxonomy}
      >
        <div className="al-courses-dialog-form">
          <span className="al-courses-label">Subject</span>
          <TaxonomyPicker
            selectedId={primaryTaxonomy}
            options={taxonomyOptions}
            loading={taxonomyLoading}
            disabled={readOnly || saving}
            onSelect={setPrimaryTaxonomy}
            onOptionsChange={setTaxonomyOptions}
            onEditorOpenChange={setSubjectEditorOpen}
            listOpen={taxonomyListOpen}
            onListOpenChange={setTaxonomyListOpen}
          />

          <span className="al-courses-label">Standard</span>
          <TaxonomyPicker
            catalog="standard"
            multiple
            selectedIds={taxonomyIds}
            options={standardOptions}
            loading={standardLoading}
            disabled={readOnly || saving}
            scopeId={primaryTaxonomy}
            scopeOptions={taxonomyOptions}
            onSelectIds={setTaxonomyIds}
            onOptionsChange={setStandardOptions}
            onEditorOpenChange={setStandardEditorOpen}
          />

          <div className="al-courses-assign-actions">
            <button type="button" className="al-courses-btn" onClick={closeTaxonomy}>
              Done
            </button>
          </div>
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={discardOpen}
        type="warning"
        icon={<TriangleAlert />}
        title="Discard unsaved changes?"
        message="Edits to this question will be lost."
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1300}
        style={COURSE_DIALOG_STYLE}
        onClose={() => setDiscardOpen(false)}
      >
        <div className="al-courses-assign-actions">
          <button
            type="button"
            className="al-cb-btn al-cb-btn--ghost"
            onClick={() => setDiscardOpen(false)}
          >
            Keep editing
          </button>
          <button
            type="button"
            className="al-cb-btn al-cb-btn--danger"
            onClick={() => {
              setDiscardOpen(false);
              finishClose();
            }}
          >
            Discard
          </button>
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={revertOpen}
        type="warning"
        icon={<TriangleAlert />}
        title="Revert to Draft?"
        message={
          draftGaps.length
            ? `This question is incomplete and cannot stay Live. Saving will revert it to Draft so students will not see it. ${draftGaps.join(" ")}`
            : "This question is incomplete and cannot stay Live. Saving will revert it to Draft so students will not see it."
        }
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1300}
        style={COURSE_DIALOG_STYLE}
        onClose={() => setRevertOpen(false)}
      >
        <div className="al-courses-assign-actions">
          <button
            type="button"
            className="al-cb-btn al-cb-btn--ghost"
            onClick={() => setRevertOpen(false)}
          >
            Keep editing
          </button>
          <button
            type="button"
            className="al-cb-btn"
            disabled={saving}
            onClick={() => {
              setRevertOpen(false);
              void persist(false, true);
            }}
          >
            Revert and save
          </button>
        </div>
      </NotificationModal>
    </>
  );
}
